import React, { useEffect, useRef, useState } from 'react';
import { AvaturnSDK } from '@avaturn/sdk';
import useCreateAvaturnSession from '../../../services/avaturn/queries/useCreateAvaturnSession';
import useUpdateAvaturnUser from '../../../services/avaturn/queries/useUpdateAvaturnUser';
import { useToast } from '../../../components/toast/useToast';
import { NewAvaturnSession } from '../../../services/avaturn/avaturn.types';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../routers/Routes';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

interface AvatarCreatorProps {
  avaturnId: string;
}

const AvaturnCreator = ({ avaturnId }: AvatarCreatorProps) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history?.state?.usr?.from) {
      navigate(window.history.state.usr.from, { replace: true });
    } else {
      navigate(ROUTES.LOBBY, { replace: true });
    }
  };

  const sdk = new AvaturnSDK();
  const initializeAvaturnSdk = async (url: string) => {
    if (containerRef.current) {
      let isExportHandled = false;

      await sdk
        .init(containerRef.current, {
          url,
        })
        .then(() => {
          sdk.on('export', (data) => {
            if (isExportHandled) return;
            isExportHandled = true;

            setIsOverlayVisible(true);
            toast({
              description: 'Avatar created!',
              variant: 'success',
            });

            saveAvaturnAvatar(
              {
                id: avaturnId,
                avaturnUser: { modelUrl: data.url, avatarId: data.avatarId },
              },
              {
                onSuccess: () => {
                  setTimeout(() => {
                    handleGoBack();
                  }, 5000);
                },
              },
            );
          });
        })
        .catch((reason) => {
          toast({
            description: 'Something went wrong, try again.',
            variant: 'default',
          });
        })
        .finally(() => {
          setIsOverlayVisible(false);
        });
    }
  };

  const { mutate: createAvaturnSession } = useCreateAvaturnSession();
  const { mutate: saveAvaturnAvatar } = useUpdateAvaturnUser();
  const { toast } = useToast();

  useEffect(() => {
    const sessionType: NewAvaturnSession = {
      avaturnId,
      sessionType: 'create_or_edit_existing',
    };

    createAvaturnSession(sessionType, {
      onSuccess: async ({ data }) => {
        console.log(data);
        await initializeAvaturnSdk(data.url);
      },
      onError: (error) => {
        toast({
          description: 'Avatar customization is not available at the moment. Try again later.',
          variant: 'default',
        });
      },
    });
  }, [avaturnId]);

  return (
    <div className="relative z-40 h-dvh w-full">
      {isOverlayVisible && (
        <div className="fixed inset-0 z-50 flex select-none flex-col items-center justify-center bg-ph-purple bg-opacity-50 text-center backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <LoadingSpinner size="10" borderColor="border-ph-gray-white" />
            <h1 className="animate-pulse font-manrope font-medium text-ph-gray-white">
              Rendering avatar, this can take up to a minute
            </h1>
          </div>
        </div>
      )}

      <div ref={containerRef} id="avaturn-sdk-container" className="z-50 h-full w-full"></div>
    </div>
  );
};

export default AvaturnCreator;
